import PropTypes from 'prop-types';
import React, {Component} from 'react';
import ModalStructure from './ModalStructure';
import {TrapFocus} from '@homeaway/a11y-tools';
import {checkElementType} from './utils';
import './Modal.less';
import './Modal-variations.less';
import './Modal-animations.less';

const KEYCODES = {
    ESCAPE: 27,
    TAB: 9
};

const TIMEOUTS = {
    backdrop: {
        transitionEnterTimeout: 250,
        transitionExitTimeout: 250
    },
    modal: {
        transitionEnterTimeout: 350,
        transitionExitTimeout: 350
    }
};

const STATE_ENUM = {
    OPEN: 1,
    CLOSING: 2
};

class Modal extends Component {
    static propTypes = {
        ...ModalStructure.propTypes,
        /** Flag to determine if the Modal should close on pressing the Esc key. */
        closeOnEsc: PropTypes.bool,
        /** Flag to determine if the Modal should close if you click outside of it. */
        closeOnOutsideClick: PropTypes.bool,
        /** Flag to determine if body should be given a fixed position to stop scroll-behind. */
        fixedBody: PropTypes.bool,
        /** Should the modal display or not. */
        isOpen: PropTypes.bool.isRequired,
        /** Event listener to fire when the Modal has opened. */
        onModalOpen: PropTypes.func,
        /** Event listener to fire when the Modal has closed. */
        onModalClose: PropTypes.func,
        /** Event handler or action that manages changing isOpen to false within your app. */
        onClose: PropTypes.func.isRequired,
        /** The DOM element to use as the parent for the Modal when visible. This is useful in scenarios where the Modal needs to break out of its current container due to overflow or z-index issues. */
        parentNode: checkElementType,
        /** Allows you to add a classname to the Modal Portal for custom styling */
        portalClassName: PropTypes.string,
        /** A flag for determining if the modal should call scrollToTop function on property update. */
        scrollTop: PropTypes.bool,
        /** Function to be called when scrollTop is true and modal properties update. */
        scrollToTop: PropTypes.func
    };

    static defaultProps = {
        ...ModalStructure.defaultProps,
        closeButton: true,
        closeOnEsc: true,
        closeOnOutsideClick: true,
        closeText: 'Close',
        fixedBody: true,
        header: true,
        isInstructional: false,
        isLoading: false,
        isOpen: false,
        modalAnimation: 'modal-from-center',
        scrollTop: false,
        scrollToTop: () => {}
    };

    constructor(props) {
        super(props);

        this.openAnimation = false;
        this.stackId = `${this.props.id}-modal-open`;
        this.focusedElementBeforeOpen = {};
        this.trapFocus = new TrapFocus();

        // Initialize global vars
        if (typeof window !== 'undefined') {
            window.reactModalLastPageYOffset =
                window.reactModalLastPageYOffset || 0;
        }
    }

    componentDidMount() {
        if (this.props.isOpen) {
            this.handleOpen();
        }
    }

    // Only update open modals.
    // Child content (footer and children) were causing all modals to update as state is shared
    shouldComponentUpdate(nextProps) {
        if (
            nextProps.isOpen === true ||
            this.props.isOpen !== nextProps.isOpen ||
            nextProps.newHeaderStyle !== this.props.newHeaderStyle
        ) {
            return true;
        }

        return false;
    }

    componentDidUpdate(prevProps) {
        let callDetermineFocusable = true;
        let shouldOpen = false;
        let shouldClose = false;

        if (this.props.isOpen !== prevProps.isOpen) {
            if (this.props.isOpen) {
                shouldOpen = true;
            } else {
                shouldClose = true;
            }
        }

        if (shouldOpen) {
            callDetermineFocusable = false;
            shouldOpen = false;
            this.handleOpen();
        } else if (shouldClose) {
            callDetermineFocusable = false;
            shouldClose = false;
            this.handleClose();
        } else if (this.props.scrollTop) {
            this.props.scrollToTop(this.modalBodyElement);
        }

        // a11y: regenerate list of focusable elements.  Not called for shouldOpen
        // as it is already called in handleOpen().  Not called in shouldClose
        // because -- it should close!
        if (
            this.modalStructure &&
            this.modalStructure.dialog &&
            callDetermineFocusable
        ) {
            this.trapFocus.determineFocusable(this.modalStructure.dialog);
        }
    }

    componentWillUnmount() {
        if (this.props.isOpen) {
            this.handleClose();
        }
    }

    handleOpen = () => {
        this.addBodyClasses();

        if (typeof window !== 'undefined') {
            this.focusedElementBeforeOpen = window.document.activeElement;
            this.addListeners();
            this.trapFocus.determineFocusable(this.modalStructure.dialog);
            this.handleFirstFocus();
        }

        if (this.props.onModalOpen) {
            this.props.onModalOpen(this.modalStructure, this.modalBodyElement);
        }
    };

    handleClose = () => {
        this.removeBodyClasses();

        if (typeof window !== 'undefined') {
            // ensure that that focusedElementBefore is an element with focus()
            // a11y: return focus to item that was focused before opening modal
            if (
                this.focusedElementBeforeOpen &&
                typeof this.focusedElementBeforeOpen.focus === 'function'
            ) {
                this.focusedElementBeforeOpen.focus();
            }

            this.removeListeners();
        }

        if (this.props.onModalClose) {
            this.props.onModalClose();
        }
    };

    /** a11y: ensure Modal dialog receives focus for SRs. */
    handleFirstFocus = () => {
        this.modalStructure.dialog.focus();
    };

    /* Resets the focused element to the first focusable element within the modal */
    resetFocus = () => {
        this.trapFocus.determineFocusable(this.modalStructure.dialog);
        this.trapFocus.resetFocus();
    };

    handleKeydown = (e) => {
        if (e.keyCode === KEYCODES.ESCAPE && this.props.isOpen) {
            this.props.onClose();
        } else if (e.keyCode === KEYCODES.TAB) {
            this.trapFocus.handleTabbing(e);
        }
    };

    addListeners = () => {
        if (typeof window !== 'undefined' && this.props.closeOnEsc) {
            window.document.addEventListener('keydown', this.handleKeydown);
        }
    };

    removeListeners = () => {
        if (this.props.closeOnEsc) {
            window.document.removeEventListener('keydown', this.handleKeydown);
        }
    };

    /**
     * setLastPageYOffset()
     * Sets globally accessible pageYOffset
     */
    setLastPageYOffset = () => {
        window.reactModalLastPageYOffset = window.pageYOffset;
    };

    /**
     * getLastPageYOffset()
     * Gets globally accessible pageYOffset
     */
    getLastPageYOffset = () => {
        return window.reactModalLastPageYOffset;
    };

    /**
     * getBodyDataSet()
     * Gets global modal data
     */
    getBodyDataset = () => {
        const dataSet = window.document.body.dataset.reactModals || '{}';
        let reactModals = JSON.parse(dataSet);
        return reactModals;
    };

    /**
     * setBodyDataSet()
     * Sets global modal data
     */
    setBodyDataset = (reactModals) => {
        window.document.body.dataset.reactModals = JSON.stringify(reactModals);
    };

    /**
     * addBodyDataset()
     * Adds modal instance to global data
     */
    addBodyDataset = () => {
        // Get global modal data
        let reactModals = this.getBodyDataset();

        // Update instance to `OPEN` state
        reactModals[this.stackId] = STATE_ENUM.OPEN;

        // Set global modal data
        this.setBodyDataset(reactModals);
    };

    /**
     * removeBodyDataset()
     * Removes modal instance from global data
     */
    removeBodyDataset = () => {
        // Get global modal data
        let reactModals = this.getBodyDataset();

        // Update instance to `CLOSING` state.
        reactModals[this.stackId] = STATE_ENUM.CLOSING;

        // Set global modal data
        this.setBodyDataset(reactModals);

        // While modal is transitioning out, set a timeout to allow for a brief `CLOSING` state.
        setTimeout(() => {
            // Get global modal data
            reactModals = this.getBodyDataset();

            // Remove instance from global modal data if it hasn't been re-opened again
            if (reactModals[this.stackId] === STATE_ENUM.CLOSING) {
                // Commit delete
                delete reactModals[this.stackId];
                this.setBodyDataset(reactModals);
            }
        }, TIMEOUTS.modal.transitionExitTimeout + 10);
    };

    /**
     * hasModalsOpen()
     * Return true if `window.document.body.dataset.reactModals[]` has any modal data with open modal state (STATE_ENUM.OPEN)
     */
    hasModalsOpen = () => {
        if (typeof window !== 'undefined') {
            // Get global modal data
            const reactModals = this.getBodyDataset();

            // Filter global modal data by `OPEN` state
            const reactModalsOpen = Object.keys(reactModals).filter((v) => {
                return reactModals[v] === STATE_ENUM.OPEN;
            });

            // Return true if any modal instance data with `OPEN` state detected.
            return reactModalsOpen.length > 0;
        }

        return false;
    };

    /**
     * hasModalsOpen()
     * Return true if `window.document.body.dataset.reactModals[]` has any modal data with open modal state (STATE_ENUM.CLOSING)
     */
    hasModalsClosing = () => {
        if (typeof window !== 'undefined') {
            // Get global modal data
            const reactModals = this.getBodyDataset();

            // Filter global modal data by `CLOSING` state
            const reactModalsOpen = Object.keys(reactModals).filter((v) => {
                return reactModals[v] === STATE_ENUM.CLOSING;
            });

            // Return true if any modal instance data with `CLOSING` state is detected
            return reactModalsOpen.length > 0;
        }
        return false;
    };

    /**
     * addBodyClasses()
     * Adds CSS classes to body and dialog elements
     */
    addBodyClasses = () => {
        if (typeof window !== 'undefined') {
            window.document.body.classList.add('body--modal-open');

            // If modals aren't already open add necessary CSS classes to document.body element
            if (this.props.fixedBody && !this.hasModalsOpen()) {
                // Only store pageYOffset while we don't have fixed-position modal classes
                // attached to body to avoid obscure paint error in mobile Safari when
                // immediately opening another modal (just use previously store value)
                if (!this.hasModalsClosing()) {
                    this.setLastPageYOffset();
                    // Set a delay for adding fixed-position CSS classes to body to hide background
                    // page jumping from the user.
                    setTimeout(
                        this.addMobileBodyClass,
                        TIMEOUTS.modal.transitionEnterTimeout
                    );
                } else {
                    this.addMobileBodyClass();
                }
            }
            // Add modal data to global shared state
            this.addBodyDataset();

            // Add modal CSS class to modal element
            this.addModalBodyParentClasses();
        }
    };

    /**
     * addMobileBodyClass()
     * Adds a body CSS class for mobile
     */
    addMobileBodyClass = () => {
        // UITK-307 - Prevent race condition if addMobileBodyClass called after a close event
        if (this.hasModalsOpen()) {
            window.document.body.classList.add('body--ios-modal-open');
        }
    };

    /**
     * removeBodyClasses()
     * Removes body CSS classes and instance from global data
     */
    removeBodyClasses = () => {
        if (typeof window !== 'undefined') {
            // Remove modal instance from global data
            // (will update state to CLOSING, wait a short delay, then finally remove data)
            this.removeBodyDataset();
            // Remove modal CSS classes from element
            this.removeModalBodyParentClasses();

            // If modals aren't open, remove body CSS classes
            if (!this.hasModalsOpen()) {
                // return to original opening position, if scrolled on iOS (iOS defect)
                window.document.body.classList.remove('body--modal-open');

                if (this.props.fixedBody) {
                    window.document.body.classList.remove(
                        'body--ios-modal-open'
                    );
                    // Scroll to previous pageYOffset when modal first opened
                    window.scrollTo(0, this.getLastPageYOffset());
                }
            }
        }
    };

    /**
     * addModalBodyParentClasses()
     * Add CSS classes to modal elements.
     */
    addModalBodyParentClasses() {
        let openModalBodies = window.document.querySelectorAll('.Modal__body');

        // for loop to handle IE 11
        for (let i = 0; i < openModalBodies.length; i++) {
            if (i !== openModalBodies.length - 1) {
                openModalBodies[i].classList.add(
                    'Modal__body--is-modal-parent'
                );
            }
        }
    }

    /**
     * removeModalBodyParentClasses()
     * Removes CSS classes from modal elements.
     */
    removeModalBodyParentClasses() {
        let openModalBodies = window.document.querySelectorAll('.Modal__body');

        // for loop to handle IE 11
        for (let i = 0; i < openModalBodies.length; i++) {
            if (i !== openModalBodies.length - 1) {
                openModalBodies[i].classList.remove(
                    'Modal__body--is-modal-parent'
                );
            }
        }
    }

    render() {
        const {
            // grab unused vars to prevent rest from being passed down as propertys on the modal div
            onModalOpen, // eslint-disable-line no-unused-vars
            onModalClose, // eslint-disable-line no-unused-vars
            scrollTop, // eslint-disable-line no-unused-vars
            scrollToTop, // eslint-disable-line no-unused-vars
            ...rest
        } = this.props;
        return (
            <ModalStructure
                ref={(c) => (this.modalStructure = c)}
                modalBodyRef={(el) => (this.modalBodyElement = el)} // eslint-disable-line react/jsx-no-bind
                timeouts={TIMEOUTS}
                {...rest}
            />
        );
    }
}

export default Modal;
